import { drawNetworkConnections } from "./network-view";

type Edge = { sourceId: number; targetId: number };
type Offset = { x: number; y: number };
type Drag = { node: HTMLElement; pointer: number; x: number; y: number; offset: Offset };
let current: { graph: HTMLElement; key: string; abort: AbortController; observer: ResizeObserver;
  offsets: Map<HTMLElement, Offset>; edges: Edge[]; frame: number; drag?: Drag } | undefined;

// Let the browser paint before synchronous WASM/driver work.
export function paintNetworkStage(): Promise<void> {
  return new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}
function redraw(): void {
  if (current) drawNetworkConnections({ edges: current.edges }, current.graph);
}
function schedule(): void {
  if (!current || current.frame) return;
  current.frame = requestAnimationFrame(() => { if (current) { current.frame = 0; redraw(); } });
}
function move(node: HTMLElement, desired: Offset): void {
  const state = current; if (!state) return;
  const graph = state.graph.getBoundingClientRect(), rect = node.getBoundingClientRect();
  const old = state.offsets.get(node) ?? { x: 0, y: 0 };
  const baseX = rect.left - graph.left - old.x, baseY = rect.top - graph.top - old.y;
  const inputs = Array.from(state.graph.querySelectorAll<HTMLElement>(".network-input"), n => n.getBoundingClientRect());
  const outputs = Array.from(state.graph.querySelectorAll<HTMLElement>(".network-output"), n => n.getBoundingClientRect());
  if (!inputs.length || !outputs.length) return;
  const gap = 8;
  let left = gap, top = gap, right = graph.width - rect.width - gap, bottom = graph.height - rect.height - gap;
  if (state.graph.classList.contains("network-graph-bottom-to-top")) {
    top = Math.max(...outputs.map(r => r.bottom)) - graph.top + gap;
    bottom = Math.min(...inputs.map(r => r.top)) - graph.top - rect.height - gap;
  } else {
    left = Math.max(...inputs.map(r => r.right)) - graph.left + gap;
    right = Math.min(...outputs.map(r => r.left)) - graph.left - rect.width - gap;
  }
  const offset = { x: Math.min(Math.max(baseX + desired.x, left), Math.max(left, right)) - baseX,
    y: Math.min(Math.max(baseY + desired.y, top), Math.max(top, bottom)) - baseY };
  state.offsets.set(node, offset);
  node.style.transform = `translate(${offset.x}px, ${offset.y}px)`;
}
export function resetNetworkPositions(): void {
  if (!current) return;
  for (const node of current.offsets.keys()) node.style.removeProperty("transform");
  current.offsets.clear(); redraw();
}
export function disposeNetworkInteraction(): void {
  if (!current) return;
  current.abort.abort(); current.observer.disconnect(); cancelAnimationFrame(current.frame);
  if (current.drag) {
    current.drag.node.classList.remove("network-dragging");
    if (current.drag.node.hasPointerCapture(current.drag.pointer)) current.drag.node.releasePointerCapture(current.drag.pointer);
  }
  resetNetworkPositions(); current = undefined;
}
export function prepareNetworkInteraction(snapshot: { edges: Edge[] }, key: string): void {
  const graph = document.getElementById("viewer-network-graph"); if (!graph) return;
  const layoutKey = `${key}:${graph.classList.contains("network-graph-bottom-to-top")}`;
  if (current?.graph === graph && current.key === layoutKey) { current.edges = snapshot.edges; redraw(); return; }
  disposeNetworkInteraction();
  const abort = new AbortController();
  const observer = new ResizeObserver(() => {
    if (!current) return;
    for (const [node, offset] of current.offsets) move(node, offset);
    schedule();
  });
  current = { graph, key: layoutKey, abort, observer, offsets: new Map(), edges: snapshot.edges, frame: 0 };
  observer.observe(graph);
  graph.addEventListener("pointerdown", event => {
    if (!current || current.drag || event.button !== 0) return;
    const node = (event.target as Element).closest<HTMLElement>(".network-hidden");
    if (!node || !graph.contains(node)) return;
    event.preventDefault();
    current.drag = { node, pointer: event.pointerId, x: event.clientX, y: event.clientY,
      offset: current.offsets.get(node) ?? { x: 0, y: 0 } };
    node.classList.add("network-dragging"); node.setPointerCapture(event.pointerId);
  }, { signal: abort.signal });
  graph.addEventListener("pointermove", event => {
    const drag = current?.drag; if (!drag || event.pointerId !== drag.pointer) return;
    event.preventDefault();
    move(drag.node, { x: drag.offset.x + event.clientX - drag.x, y: drag.offset.y + event.clientY - drag.y }); schedule();
  }, { signal: abort.signal });
  const finish = (event: PointerEvent) => {
    const drag = current?.drag; if (!drag || drag.pointer !== event.pointerId) return;
    current!.drag = undefined;
    drag.node.classList.remove("network-dragging");
    if (drag.node.hasPointerCapture(drag.pointer)) drag.node.releasePointerCapture(drag.pointer);
    redraw();
  };
  for (const type of ["pointerup", "pointercancel", "lostpointercapture"] as const) graph.addEventListener(type, finish, { signal: abort.signal });
  redraw();
}
Object.assign(window, { networkViewerUi: { paint: paintNetworkStage, prepare: prepareNetworkInteraction,
  reset: resetNetworkPositions, dispose: disposeNetworkInteraction } });
